// This component is a customized version of SidebarSearchModal from @backstage/plugin-search.
// https://github.com/backstage/backstage/blob/master/plugins/search/src/components/SearchModal/SearchModal.tsx

import React from 'react';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import qs from 'qs';
import { useContent } from '@backstage/core-components';
import { useRouteRef, useAnalytics } from '@backstage/core-plugin-api';
import {
  SearchContextProvider,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import { SearchResultSet } from '@backstage/plugin-search-common';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { Text } from '@backstage/ui';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';

import { searchRouteRef } from '../../routes.ts';
import { captureSearchEvent, parseSearchQuery } from '../../utils';
import { CustomSearchBar } from '../CustomSearchBar';

/**
 * @public
 */
export interface SearchModalChildrenProps {
  /**
   * A function that should be invoked when navigating away from the modal.
   */
  toggleModal: () => void;

  /**
   * Ability to provide custom components to render the result items
   */
  resultItemComponents?:
    | ReactNode
    | ((resultSet: SearchResultSet) => JSX.Element);
}

/**
 * @public
 */
export interface SearchModalProps {
  /**
   * If true, it renders the modal.
   */
  open?: boolean;
  /**
   * This is supposed to be used together with the open prop.
   * If `hidden` is true, it hides the modal.
   * If `open` is false, the value of `hidden` has no effect on the modal.
   * Use `open` for controlling whether the modal should be rendered or not.
   */
  hidden?: boolean;
  /**
   * a function invoked when a search item is pressed or when the dialog
   * should be closed.
   */
  toggleModal: () => void;
  /**
   * A function that returns custom content to render in the search modal in
   * place of the default.
   */
  children?: (props: SearchModalChildrenProps) => JSX.Element;

  /**
   * Optional ability to pass in result item component renderers.
   */
  resultItemComponents?: SearchModalChildrenProps['resultItemComponents'];
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    gap: theme.spacing(1),
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto',
    '&> button': {
      marginTop: theme.spacing(1),
    },
  },
  input: {
    flex: 1,
  },
  button: {
    '&:hover': {
      background: 'none',
    },
    '&:focus': {
      background: theme.palette.background.default
    }
  },
  // Reduces default height of the modal, keeping a gap of 128px between the top and bottom of the page.
  paperFullWidth: { height: 'calc(100% - 128px)' },
  dialogActionsContainer: { padding: theme.spacing(1, 3) },
  viewResultsLink: { verticalAlign: '0.5em' },
  filterHint: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

export const Modal = ({
                        toggleModal,
                        resultItemComponents,
                      }: SearchModalChildrenProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { transitions } = useTheme();
  const { focusContent } = useContent();

  const searchRootRoute = useRouteRef(searchRouteRef)();
  const searchBarRef = useRef<HTMLInputElement | null>(null);
  const analytics = useAnalytics();
  const { term, types, result, filters, setFilters, setTerm } = useSearch();
  
  // Local state to store the raw input value (including filter syntax)
  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    searchBarRef?.current?.focus();
  });

  // Parse the input value and apply filters to the search context
  useEffect(() => {
    const parsed = parseSearchQuery(inputValue);
    
    // Apply filters to search context
    setFilters(parsed.filters);
    
    // Set the term to the parsed term (without filter syntax)
    setTerm(parsed.term);
  }, [inputValue, setFilters, setTerm]);

  // Capture search event when results load
  useEffect(() => {
    captureSearchEvent(analytics, result, term, types, filters);
  }, [result, term, types, filters, analytics]);

  const handleSearchResultClick = useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
    // Capture discover event if event is provided
    if (event) {
      const target = event.currentTarget as HTMLElement;
      const resultTitle = target.textContent?.substring(0, 100) || 'search-result';
      const resultLink = target.getAttribute('href') || '';

      analytics.captureEvent('discover', resultTitle, {
        attributes: { to: resultLink },
      });
    }

    setTimeout(focusContent, transitions.duration.leavingScreen);
  }, [focusContent, transitions, analytics]);

  // This handler is called when "enter" is pressed
  const handleSearchBarSubmit = useCallback(() => {
    // Parse the input to extract filters and term
    const parsed = parseSearchQuery(inputValue);

    // Build structured query parameters using qs library
    // This matches the format expected by Backstage's SearchPage UrlUpdater
    const queryParams = qs.stringify(
      {
        query: parsed.term,
        filters: parsed.filters,
      },
      { arrayFormat: 'brackets' }
    );

    navigate(`${searchRootRoute}?${queryParams}`);
    handleSearchResultClick();
  }, [navigate, handleSearchResultClick, searchRootRoute, inputValue]);
  
  // Handle changes to the search input
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  return (
    <>
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <Box className={classes.input}>
            <CustomSearchBar
              ref={searchBarRef}
              value={inputValue}
              onChange={handleInputChange}
              onSubmit={handleSearchBarSubmit}
              placeholder="Try searching: 'api name:suzaku' 'backchannel name:navis' 'owner:hermes_reviewers'"
              className={classes.input}
            />
          </Box>

          <IconButton aria-label="close" onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          direction="row-reverse"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item>
            <Button
              className={classes.button}
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleSearchBarSubmit}
              disableRipple
            >
              ✨ Advanced Search ✨
            </Button>
          </Grid>
          <Grid item>
            <Text as="div" weight="bold">
              💡 To search items by more detailed filters, please use
            </Text>
          </Grid>
        </Grid>
        <Divider />
        <SearchResult
          onClick={handleSearchResultClick}
          onKeyDown={handleSearchResultClick}
        >
          {resultItemComponents}
        </SearchResult>
      </DialogContent>
      <DialogActions className={classes.dialogActionsContainer}>
        <Grid container direction="row">
          <Grid item xs={12}>
            <SearchResultPager />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

/**
 * @public
 */
export const CustomSearchModal = (props: SearchModalProps) => {
  const {
    open = true,
    hidden,
    toggleModal,
    children,
    resultItemComponents,
  } = props;

  const classes = useStyles();

  return (
    <Dialog
      classes={{
        paperFullWidth: classes.paperFullWidth,
      }}
      onClose={toggleModal}
      aria-label="Search Modal"
      aria-modal="true"
      fullWidth
      maxWidth="lg"
      open={open}
      hidden={hidden}
    >
      {open && (
        <SearchContextProvider inheritParentContextIfAvailable>
          {(children &&
            children({
              toggleModal,
              resultItemComponents: resultItemComponents || [],
            })) ?? (
            <Modal
              toggleModal={toggleModal}
              resultItemComponents={resultItemComponents}
            />
          )}
        </SearchContextProvider>
      )}
    </Dialog>
  );
};